import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { createColumnHelper, flexRender, useTable } from "@tanstack/react-table"
import axios from "axios"
import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "design-system/molecules/DropdownMenu"
import { Section } from "design-system/molecules/Section"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { CopyButton } from "design-system/organisms/chat/CopyButton"
import { features, type Features } from "lib/table"
import { Ellipsis, LockKeyhole, Pencil, Plus, Trash } from "lucide-react"
import { useAuthEmployeeQuery } from "modules/auth/hooks/useAuthEmployeeQuery"
import { useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { VaultEntryResponseData } from "schema"
import { toast } from "sonner"
import { useVaultEntriesQuery } from "../../hooks/useVaultEntriesQuery"
import { vaultQuery } from "../../lib/vaultQuery"

const columnHelper = createColumnHelper<Features, VaultEntryResponseData>()

export function CompanyVault() {
  const { t } = useTranslation()
  const { data: employee } = useAuthEmployeeQuery()
  const { data: entries, isPending } = useVaultEntriesQuery()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [deletingKey, setDeletingKey] = useState<string | null>(null)
  const canManage = Boolean(employee?.isOwner || employee?.isManager)

  const deleteEntry = useMutation({
    mutationFn: async (key: string) => {
      await axios.delete(`/vault/${encodeURIComponent(key)}`)
    },
    onSuccess: async () => {
      setDeletingKey(null)
      await queryClient.invalidateQueries({ queryKey: vaultQuery().queryKey })
      toast.success(t("settings.keysAndSecrets.vault.deleted"))
    },
  })

  const columns = columnHelper.columns([
    columnHelper.accessor("key", {
      header: t("settings.keysAndSecrets.vault.key"),
      cell: (key) => (
        <div className="flex flex-row items-center gap-1">
          <span className="font-mono">{key.getValue()}</span>
          <CopyButton
            content={`{{ vault.${key.getValue()} }}`}
            copyMessage={t("settings.keysAndSecrets.vault.copied")}
            positioning={false}
          />
        </div>
      ),
    }),
    columnHelper.accessor("value", {
      header: t("settings.keysAndSecrets.vault.value"),
      cell: (value) =>
        value.row.original.isSecret ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <LockKeyhole size={16} />
            {t("settings.keysAndSecrets.vault.hidden")}
          </div>
        ) : (
          <span className="font-mono break-all">{value.getValue()}</span>
        ),
      meta: {
        colSpan: 2,
      },
    }),
    columnHelper.accessor("isSecret", {
      header: t("settings.keysAndSecrets.vault.type"),
      cell: (isSecret) => (
        <Badge variant={isSecret.getValue() ? "secondary" : "outline"}>
          {t(
            isSecret.getValue()
              ? "settings.keysAndSecrets.vault.secret"
              : "settings.keysAndSecrets.vault.plain",
          )}
        </Badge>
      ),
    }),
    columnHelper.display({
      id: "actions",
      cell: (props) =>
        canManage && (
          <VaultEntryDropdown
            isSecret={props.row.original.isSecret}
            onReplace={() =>
              navigate({
                to: "/settings/keys-and-secrets/vault/$key/replace",
                params: { key: props.row.original.key },
              })
            }
            onDelete={() => setDeletingKey(props.row.original.key)}
          />
        ),
      meta: {
        align: "right",
      },
    }),
  ])

  const table = useTable({
    data: entries ?? [],
    columns,
    features,
  })

  function renderRows() {
    if (isPending || !entries) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (entries.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("settings.keysAndSecrets.vault.empty")}
        />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow key={row.id}>
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} {...cell.column.columnDef.meta}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  return (
    <Section
      title={t("settings.keysAndSecrets.vault.title")}
      description={
        <Trans
          i18nKey="settings.keysAndSecrets.vault.description"
          values={{ reference: "{{ vault.KEY }}" }}
          components={{ code: <code className="font-mono" /> }}
        />
      }
      action={
        canManage && (
          <Button onClick={() => navigate({ to: "/settings/keys-and-secrets/vault/create" })}>
            <Plus size={18} />
            {t("settings.keysAndSecrets.vault.add")}
          </Button>
        )
      }
    >
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} {...header.column.columnDef.meta}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>{renderRows()}</TableBody>
      </Table>
      <Dialog open={deletingKey !== null} onOpenChange={(open) => !open && setDeletingKey(null)}>
        <DialogContent className="sm:max-w-[475px]">
          <DialogHeader>
            <DialogTitle>
              {t("settings.keysAndSecrets.vault.deleteTitle", { key: deletingKey })}
            </DialogTitle>
            <DialogDescription>
              {t("settings.keysAndSecrets.vault.deleteConfirm")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setDeletingKey(null)}>
              {t("shared.cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleteEntry.isPending}
              onClick={() => deletingKey && deleteEntry.mutate(deletingKey)}
            >
              {t("settings.keysAndSecrets.vault.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  )
}

type DropdownProps = {
  isSecret: boolean
  onReplace: () => void
  onDelete: () => void
}

function VaultEntryDropdown({ isSecret, onReplace, onDelete }: DropdownProps) {
  const { t } = useTranslation()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" title={t("settings.keysAndSecrets.vault.actions")}>
          <Ellipsis size={20} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem asChild onClick={onReplace}>
          <div className="flex flex-row gap-2 items-center">
            <Pencil size={16} />
            {t(
              isSecret
                ? "settings.keysAndSecrets.vault.replace"
                : "settings.keysAndSecrets.vault.edit",
            )}
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem asChild onClick={onDelete}>
          <div className="flex flex-row gap-2 items-center">
            <Trash size={16} />
            {t("settings.keysAndSecrets.vault.delete")}
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
